import React, { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Tag } from 'lucide-react';
import { PromoCode } from '@/hooks/sms/types';
import { usePromoCodesManager } from '@/hooks/sms/usePromoCodesManager';
import { useAuth } from '@/context/auth';

interface PromoCodeSelectorProps {
  restaurantId?: string;
  onSelectCode: (code: string, codeId: number, promoDetails?: PromoCode) => void;
  disabled?: boolean;
  variant?: 'button' | 'inline';
  messageContent?: string;
}

const PromoCodeSelector: React.FC<PromoCodeSelectorProps> = ({
  restaurantId,
  onSelectCode,
  disabled = false,
  variant = 'button',
  messageContent = ''
}) => {
  const { user } = useAuth();
  
  // Memoize the selectedRestaurants array to prevent infinite re-renders
  const selectedRestaurants = useMemo(() => {
    return restaurantId ? [restaurantId] : [];
  }, [restaurantId]);
  
  const { promoCodes, isLoading, error } = usePromoCodesManager(user, selectedRestaurants);

  const formatDiscount = (promoCode: PromoCode) => {
    switch (promoCode.discount_type) {
      case 'percentage_off':
        return `${promoCode.discount_value}% off`;
      case 'dollar_off':
        return `$${promoCode.discount_value} off`;
      case 'free_item':
        return 'Free item';
      default:
        return 'Discount';
    }
  };

  // Filter to only active codes and check if they're not expired
  const activeCodes = promoCodes.filter(code => {
    if (!code.active) return false;
    
    const now = new Date();
    if (code.end_date && new Date(code.end_date) < now) return false;
    if (code.start_date && new Date(code.start_date) > now) return false;
    
    return true;
  });

  const isCodeInMessage = (code: string) => {
    return messageContent.includes(code);
  };

  if (!restaurantId) {
    return (
      <Button variant="outline" size="sm" disabled className="opacity-50">
        <Tag className="h-4 w-4 mr-2" />
        Select Restaurant First
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          disabled={disabled || isLoading || activeCodes.length === 0}
          className="flex items-center"
        >
          <Tag className="h-4 w-4 mr-2" />
          {isLoading ? 'Loading...' : 'Promo Code'}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64 max-h-80">
        <DropdownMenuLabel>Select Promo Code</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {error ? (
          <DropdownMenuItem disabled>
            Error loading promo codes
          </DropdownMenuItem>
        ) : activeCodes.length === 0 ? (
          <DropdownMenuItem disabled>
            No active promo codes available
          </DropdownMenuItem>
        ) : (
          <div className="max-h-64 overflow-y-auto">
            {activeCodes.map((code) => (
              <DropdownMenuItem
                key={code.id}
                onClick={() => onSelectCode(code.code, code.id, code)}
                className="cursor-pointer"
              >
                <div className="flex flex-col w-full">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-sm">{code.code}</span>
                    <span className="text-xs text-muted-foreground">
                      {formatDiscount(code)}
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {code.name || `${code.discount_type.replace('_', ' ')} discount`}
                  </span>
                  {code.usage_limit && (
                    <span className="text-xs text-muted-foreground">
                      Used: {code.current_usage || 0}/{code.usage_limit}
                    </span>
                  )}
                  {isCodeInMessage(code.code) && (
                    <span className="text-xs text-green-600 font-medium">
                      ✓ Added to message
                    </span>
                  )}
                </div>
              </DropdownMenuItem>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default PromoCodeSelector;